// src/components/Form/Addemployee.jsx
// Updated to handle return from sub-pages (Designation, Type, Department) correctly.
// Also includes "Required Fields" modal and confirmation dialog.
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { FaUserPlus, FaArrowLeft, FaBriefcase, FaBuilding, FaUserCircle, FaMoneyBillWave, FaSave, FaExclamationTriangle, FaCheckCircle, FaUsers, FaPlus, FaClock, FaIdCard, FaUniversity, FaGraduationCap, FaTools, FaStethoscope, FaUserShield, FaUserTie, FaTimes, FaUser, FaUpload, FaCog, FaCalendarAlt, FaChevronLeft, FaChevronRight, FaChevronDown, FaMapMarkerAlt } from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import './CreateItemPage.css';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';

const CustomDatePicker = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [yearPage, setYearPage] = useState(() => Math.floor((currentDate.getFullYear()) / 12) * 12);

  // Popup quick input states
  const [popupDay, setPopupDay] = useState(() => value ? (new Date(value).getDate() || "") : "");
  const [popupMonth, setPopupMonth] = useState(() => value ? ((new Date(value).getMonth() + 1) || "") : "");
  const [popupYear, setPopupYear] = useState(() => value ? (new Date(value).getFullYear() || "") : "");

  useEffect(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        setCurrentDate(parsed);
        setYearPage(Math.floor(parsed.getFullYear() / 12) * 12);
        setPopupDay(parsed.getDate() || "");
        setPopupMonth((parsed.getMonth() + 1) || "");
        setPopupYear(parsed.getFullYear() || "");
      }
    } else {
      setPopupDay("");
      setPopupMonth("");
      setPopupYear("");
    }
  }, [value]);

  const handleQuickChange = (type, val) => {
    if (type === 'day') setPopupDay(val);
    if (type === 'month') setPopupMonth(val);
    if (type === 'year') setPopupYear(val);

    const newDay = type === 'day' ? val : popupDay;
    const newMonth = type === 'month' ? val : popupMonth;
    const newYear = type === 'year' ? val : popupYear;

    const parsedYear = parseInt(newYear, 10);
    const parsedMonth = parseInt(newMonth, 10);
    const parsedDay = parseInt(newDay, 10);

    if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12 && !isNaN(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      const newDateObj = new Date(parsedYear, parsedMonth - 1, parsedDay);
      if (!isNaN(newDateObj)) {
        setCurrentDate(newDateObj);
        setYearPage(Math.floor(parsedYear / 12) * 12);
        const formattedMonth = String(parsedMonth).padStart(2, '0');
        const formattedDay = String(parsedDay).padStart(2, '0');
        onChange(`${parsedYear}-${formattedMonth}-${formattedDay}`);
      }
    } else if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
      setCurrentDate(new Date(parsedYear, parsedMonth - 1, 1));
      setYearPage(Math.floor(parsedYear / 12) * 12);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleSelectDate = (day, e) => {
    e.stopPropagation();
    const formattedMonth = String(month + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const selectedDateStr = `${year}-${formattedMonth}-${formattedDay}`;
    onChange(selectedDateStr);
    setIsOpen(false);
    setView('days');
  };

  let selectedYear = null, selectedMonth = null, selectedDay = null;
  if (value) {
    const parsed = new Date(value);
    if (!isNaN(parsed)) {
      selectedYear = parsed.getFullYear();
      selectedMonth = parsed.getMonth();
      selectedDay = parsed.getDate();
    }
  }

  return (
    <div className="custom-datepicker-container" style={{ position: 'relative', width: '100%' }}>
      <div 
        className="custom-datepicker-input"
        onClick={() => { setIsOpen(!isOpen); setView('days'); }}
        style={{ position: 'relative' }}
      >
        <input 
          type="text" 
          readOnly
          value={value || ""} 
          placeholder={placeholder || "YYYY-MM-DD"} 
          style={{ cursor: 'pointer', background: '#F3F6FB', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid transparent', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }}
        />
        <FaCalendarAlt 
          style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} 
        />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-backdrop" onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: '320px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box' }}>
            
            {/* Quick Input Row inside Popup */}
            <div className="popup-quick-inputs-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 24px 0', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Enter Date</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span>
                  <input 
                    type="number" 
                    value={popupDay} 
                    onChange={(e) => handleQuickChange('day', e.target.value)} 
                    placeholder="DD" 
                    min="1" 
                    max="31"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} 
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span>
                  <input 
                    type="number" 
                    value={popupMonth} 
                    onChange={(e) => handleQuickChange('month', e.target.value)} 
                    placeholder="MM" 
                    min="1" 
                    max="12"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} 
                  />
                </div>
                <div style={{ flex: 1.5 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span>
                  <input 
                    type="number" 
                    value={popupYear} 
                    onChange={(e) => handleQuickChange('year', e.target.value)} 
                    placeholder="YYYY" 
                    min="1000" 
                    max="9999"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} 
                  />
                </div>
              </div>
            </div>

            {view === 'days' && (
              <>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={handlePrevMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronLeft />
                  </button>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {monthNames[month]}
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {year}
                    </span>
                  </div>
                  <button type="button" onClick={handleNextMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronRight />
                  </button>
                </div>

                <div className="custom-datepicker-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                  {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => (
                    <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>
                  ))}
                </div>

                <div className="custom-datepicker-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: startOffset }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = selectedYear === year && selectedMonth === month && selectedDay === day;
                    const dayOfWeek = (startOffset + i) % 7;
                    const startOfWeek = day - dayOfWeek;
                    const endOfWeek = startOfWeek + 6;
                    const isSameWeekAfterSelected = selectedYear === year && selectedMonth === month && selectedDay && selectedDay >= startOfWeek && selectedDay <= endOfWeek && day > selectedDay;

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={(e) => handleSelectDate(day, e)}
                        className={`cal-day-btn ${isSelected ? 'selected' : isSameWeekAfterSelected ? 'highlighted' : ''}`}
                        style={{
                          height: '36px',
                          width: '100%',
                          border: 'none',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: isSelected ? '700' : '600',
                          cursor: 'pointer',
                          background: isSelected ? '#604BE8' : isSameWeekAfterSelected ? '#F3F6FB' : 'transparent',
                          color: isSelected ? '#ffffff' : isSameWeekAfterSelected ? '#604BE8' : '#1B1B29',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === 'months' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setView('days'); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                  <div style={{ width: '36px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {monthNames.map((mName, index) => (
                    <button
                      key={mName}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentDate(new Date(year, index, 1));
                        setView('days');
                      }}
                      style={{
                        height: '42px',
                        border: 'none',
                        borderRadius: '10px',
                        background: index === month ? '#604BE8' : '#F3F6FB',
                        color: index === month ? '#ffffff' : '#1B1B29',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {mName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'years' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage - 12); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setView('days'); }}>
                    {yearPage} - {yearPage + 11}
                  </span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage + 12); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronRight />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const y = yearPage + i;
                    return (
                      <button
                        key={y}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentDate(new Date(y, month, 1));
                          setView('days');
                        }}
                        style={{
                          height: '42px',
                          border: 'none',
                          borderRadius: '10px',
                          background: y === year ? '#604BE8' : '#F3F6FB',
                          color: y === year ? '#ffffff' : '#1B1B29',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SearchableSelect = ({ options = [], value = '', onChange, placeholder, allowCreateNew = false, onAddNewValue = null, createNewLabel = null, onCreateRequest = null }) => {
  const [search, setSearch] = useState(String(value || ''));
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    setSearch(String(value || ''));
  }, [value]);

  const filteredOptions = options.filter(option =>
    String(option || "").toLowerCase().includes(String(search || "").toLowerCase())
  );

  const handleInputChange = (e) => {
    const newSearch = e.target.value;
    setSearch(newSearch);
    if (!showList) setShowList(true);
    if (allowCreateNew && !onAddNewValue) {
      if (onChange) onChange(newSearch);
    }
  };

  const handleSelectOption = (option) => {
    setSearch(option);
    if (onChange) onChange(option);
    setShowList(false);
  };

  const handleCreateNewOption = async () => {
    if (onCreateRequest) {
      onCreateRequest(search);
      setShowList(false);
      return;
    }
    if (onAddNewValue) {
      const valToCreate = search.trim() || (createNewLabel ? `New ${createNewLabel}` : "New Item");
      const success = await onAddNewValue(valToCreate);
      if (success) {
        setSearch(valToCreate);
        if (onChange) onChange(valToCreate);
        setShowList(false);
      }
      return;
    }
    if (allowCreateNew) {
      handleSelectOption(search.trim());
    }
  };

  const handleFocus = () => {
    setShowList(true);
    // If there's a value, clear the search so all options show up initially
    if (value) {
      setSearch('');
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowList(false);
      // Restore search to the current value
      setSearch(value || '');
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showCreateOption && search.trim() !== '') {
        handleCreateNewOption();
      } else if (filteredOptions.length > 0) {
        handleSelectOption(filteredOptions[0]);
      } else if (search.trim() !== '') {
        // Fallback for simple data entry if allowCreateNew is false but no options match
        onChange(search.trim());
        setShowList(false);
      }
    }
  };

  const isExactMatch = filteredOptions.some(option => (option || "").toLowerCase() === (search.trim()).toLowerCase());
  // Show create option if allowed, and either we are searching for something new OR the list is empty/just opened
  const showCreateOption = allowCreateNew && !isExactMatch;

  return (
    <div className="searchable-select" style={{ position: 'relative', width: '100%' }}>
      <div className="input-wrapper" style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          value={search}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{ paddingRight: '40px', width: '100%', boxSizing: 'border-box' }}
        />
        <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}>
          <FaChevronDown />
        </div>

        {showList && (
          <ul className="searchable-list" style={{ position: 'absolute', top: '100%', left: 0, width: '100%', background: '#ffffff', border: '2px solid #604BE8', zIndex: 1000, boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'block', borderRadius: '12px', marginTop: '5px', padding: 0, listStyle: 'none', overflowY: 'auto', maxHeight: '300px', boxSizing: 'border-box' }}>
            {showCreateOption && (
              <li
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCreateNewOption();
                }}
                style={{
                  padding: '14px 16px',
                  background: '#eff2f9',
                  color: '#604BE8',
                  fontWeight: '800',
                  cursor: 'pointer',
                  borderBottom: filteredOptions.length > 0 ? '2px solid #C3CDE4' : 'none'
                }}
              >
                {search.trim()
                  ? `+ Add "${search.trim()}"`
                  : `+ Add New ${createNewLabel || 'Item'}`}
              </li>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const displayLabel = String(option || "");
                return (
                  <li
                    key={index}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectOption(option);
                    }}
                    style={{
                      padding: '12px 16px',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontWeight: '700',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {displayLabel}
                  </li>
                );
              })
            ) : (
              !showCreateOption && (
                <li className="no-options" style={{ padding: '20px', color: '#475569', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                  {options.length === 0 ? "Loading Data..." : "No items found"}
                </li>
              )
            )}
          </ul>
        )}
      </div>
      {/* Dynamic Spacer: Grows just enough to accommodate the list height without being a huge fixed block */}
      {showList && <div className="expansion-spacer" style={{ height: filteredOptions.length > 5 ? '280px' : filteredOptions.length > 0 ? `${filteredOptions.length * 50 + 60}px` : '100px' }}></div>}
    </div>
  );
};

const AddEmployee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [metadata, setMetadata] = useState(null);
  const [linkedData, setLinkedData] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: '',
    gender: '',
    date_of_birth: '',
    date_of_joining: '',
    company: 'POS 8',
    status: 'Active',
    salutation: '',
    marital_status: '',
    id_number: '',
    id_expiry: '',
    address: '',
    employee_designation: '',
    employee_type: '',
    basic_salary: '',
    hra: '',
    ta: '',
    oa: '',
    total_salary: '',
    username: '',
    password: '',
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    branch_code: '',
    nationality: '',
    education: '',
    previous_experience: '',
    skills: '',
    health_info: '',
    family_details: '',
    profile_image: '',
    department: '',
    employee_id: '',
  });
  
  // Tab mapping for dynamic fields
  const getFieldTab = (fieldId) => {
    if (metadata && metadata.fields) {
      let currentSection = 'details';
      for (let i = 0; i < metadata.fields.length; i++) {
        const f = metadata.fields[i];
        if (f.type === 'Section Break') {
          currentSection = f.id;
        }
        if (f.id === fieldId) {
          return currentSection;
        }
      }
    }
    
    const mapping = {
      name: 'details', employee_id: 'details', phone_number: 'details', email: 'details', status: 'details', profile_image: 'details', 
      username: 'credentials', password: 'credentials',
      gender: 'personal', date_of_birth: 'personal', salutation: 'personal', marital_status: 'personal', id_number: 'personal', id_expiry: 'personal', nationality: 'personal', address: 'personal',
      date_of_joining: 'employment', employee_designation: 'employment', employee_type: 'employment', department: 'employment',
      basic_salary: 'salary', hra: 'salary', ta: 'salary', oa: 'salary', total_salary: 'salary',
      education: 'professional', previous_experience: 'professional', skills: 'professional',
      bank_name: 'other', account_holder_name: 'other', account_number: 'other', branch_code: 'other', health_info: 'other', family_details: 'other'
    };
    return mapping[fieldId] || 'details';
  };

  const getTabs = () => {
    if (metadata && metadata.fields) {
      const sections = metadata.fields.filter(f => f.type === 'Section Break' && !f.hidden);
      if (sections.length > 0) {
        return sections.map(s => ({ key: s.id, label: s.label }));
      }
    }
    return [
      { key: 'details', label: 'Basic Details' },
      { key: 'personal', label: 'Personal Info' },
      { key: 'employment', label: 'Employment' },
      { key: 'salary', label: 'Salary' },
      { key: 'professional', label: 'Professional' },
      { key: 'other', label: 'Other Details' },
      { key: 'credentials', label: 'Credentials' }
    ];
  };

  // State for phone number country code
  const [selectedISDCode, setSelectedISDCode] = useState("+971");
  const [showISDCodeDropdown, setShowISDCodeDropdown] = useState(false);
  // ISD Codes array
  const isdCodes = [
    { code: "+91", country: "India" },
    { code: "+1", country: "USA" },
    { code: "+44", country: "UK" },
    { code: "+971", country: "UAE" },
    { code: "+61", country: "Australia" },
  ];
  // Digit lengths per country for dynamic validation
  const digitLengths = {
    '+91': 10,
    '+1': 10,
    '+44': 10,
    '+971': 9,
    '+61': 9,
  };
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'personal', 'employment', 'salary', 'professional', 'other', 'credentials'
  const [loading, setLoading] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [customizationTarget, setCustomizationTarget] = useState("Employee");
  // --- Inline Creation Panel State ---
  const [creationPanel, setCreationPanel] = useState({ open: false, docType: null, fields: [], formValues: {}, saving: false, error: null, fieldForId: null });
  const [activeTabId, setActiveTabId] = useState("");
  const [nextIdLoading, setNextIdLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // NEW: Confirmation modal before submit
  const [showConfirmation, setShowConfirmation] = useState(false);
  // NEW: Required Fields Modal
  const [showRequiredFields, setShowRequiredFields] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);

  // Permission States
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [permModalMsg, setPermModalMsg] = useState("");
  const [showPermModal, setShowPermModal] = useState(false);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  // --- Branch Assignment State ---
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  // Dynamic API URL and User Context
  const { baseUrl, configLoading, user, getHeaders } = useContext(UserContext);
  const [userBranch, setUserBranch] = useState("");
  const [userCompany, setUserCompany] = useState("");


  const fetchMetadata = async (currentBaseUrl = baseUrl) => {
    try {
      const url = `${currentBaseUrl}/api/doctypes/Employee`;
      const response = await axios.get(url, { headers: getHeaders() });
      setMetadata(response.data);
      
      const sections = response.data.fields.filter(f => f.type === 'Section Break' && !f.hidden);
      if (sections.length > 0) {
        setActiveTab(sections[0].id);
      } else {
        setActiveTab('details');
      }

      // Fetch linked data for any Table fields
      const tableFields = response.data.fields.filter(f => f.type === 'Table' && f.link_doctype);
      for (const field of tableFields) {
        fetchLinkedData(field.link_doctype, currentBaseUrl);
      }
    } catch (err) {
      console.error("Error fetching Employee metadata:", err);
    }
  };

  const fetchLinkedData = async (docTypeName, currentBaseUrl = baseUrl) => {
    try {
      const endpointMap = {
        'Employee Designation': 'employee-designations',
        'Employee Type': 'employee-types',
        'Employee Department': 'departments'
      };
      const endpoint = endpointMap[docTypeName] || docTypeName.toLowerCase().replace(/\s+/g, '-');
      const url = `${currentBaseUrl}/api/${endpoint}`;
      const response = await axios.get(url, { headers: getHeaders() });
      setLinkedData(prev => ({ ...prev, [docTypeName]: response.data }));
    } catch (err) {
      console.error(`Error fetching linked data for ${docTypeName}:`, err);
    }
  };

  // Fetch Permissions
  const fetchPermissions = async (currentBaseUrl = baseUrl) => {
    try {
      setPermsLoading(true);
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        const isAdminRole = checkIsAdmin(userObj);
        const isGroupAdminRole = checkIsGlobalAdmin(userObj);
        
        setIsGroupAdmin(isGroupAdminRole);
        setIsCompanyAdmin(isAdminRole);
        const roleRaw = userObj.role || "";
        const isBA = roleRaw.toLowerCase().includes('branch') || roleRaw.toLowerCase().includes('manager');
        setIsBranchAdmin(isBA);

        if (isAdminRole || isGroupAdminRole) {
          fetchCompanies(currentBaseUrl);
        } else {
          const activeContext = localStorage.getItem('active_company');
          const comp = userObj.company_name || userObj.company || "";
          setSelectedCompanies([activeContext || comp]);
        }

        if (roleRaw) {
          const url = `${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`;
          const response = await axios.get(url, { headers: getHeaders() });
          const perms = response.data.permissions || [];
          const pagePerm = perms.find(p => p.pageId === 'add_employee');

          if (isAdminRole || isGroupAdminRole) {
            setCanRead(true);
            setCanWrite(true);
            setCanCreate(true);
          } else if (pagePerm) {
            setCanRead(pagePerm.canRead === true);
            setCanWrite(pagePerm.canWrite === true);
            setCanCreate(pagePerm.canCreate === true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermsLoading(false);
    }
  };

  useEffect(() => {
    if (!configLoading) {
      const url = baseUrl || "";
      const empId = location.state?.editingEmployee?._id || location.state?.editingEmployee?.id || location.state?.editingId;

      // NEW: Auto-populate company and branch from user context if not editing
      const userStr = localStorage.getItem('user');
      if (!empId && userStr) {
         const userObj = JSON.parse(userStr);
         const companyName = userObj.company_name || userObj.company;
         if (companyName) {
            if (Array.isArray(companyName)) {
               setSelectedCompanies(companyName);
            } else {
               setSelectedCompanies([companyName]);
            }
         }
         
         // Robust Branch Auto-selection for Branch Logins
         const activeBranch = localStorage.getItem('active_branch');
         const userBranch = (activeBranch && activeBranch !== 'All Branches' && activeBranch !== 'All') ? activeBranch : (userObj.branch_name || userObj.branch || "");
         const isUserAdmin = checkIsAdmin(userObj) && (!userBranch || userBranch === 'All Branches');

         if (!isUserAdmin && userBranch && userBranch !== 'All Branches') {
           setSelectedBranches([userBranch]);
           console.log("Auto-selected branch for non-admin user:", userBranch);
         }
         setUserBranch(userBranch);
         setUserCompany(companyName || "");
      }

      const promises = [
        fetchPermissions(url),
        fetchCompanies(url),
        fetchMetadata(url)
      ];

      if (empId && !location.state?.formData) {
        setEditingId(empId);
        promises.push(fetchSingleEmployee(empId, url));
      }

      Promise.all(promises);
    }
  }, [configLoading, baseUrl, user]);

  const [editingId, setEditingId] = useState(null);
  // NEW: For image upload
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  // UPDATED: Company options state
  const [companyOptions, setCompanyOptions] = useState([]); // Dynamic options
  // Status options
  const statusOptions = ['Active', 'Inactive'];
  // Salutation options
  const salutationOptions = ['', 'Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.'];
  // Marital Status options
  const maritalOptions = ['', 'Single', 'Married', 'Divorced', 'Widowed'];
  // Nationality options (example; can be select or text)
  const nationalityOptions = ['', 'Indian', 'American', 'British', 'Emirati', 'Australian', 'Other'];
  // Gender options
  const genderOptions = ['', 'Male', 'Female', 'Other'];

  // NEW: Compute total salary when components change
  useEffect(() => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const hra = parseFloat(formData.hra) || 0;
    const ta = parseFloat(formData.ta) || 0;
    const oa = parseFloat(formData.oa) || 0;
    const total = basic + hra + ta + oa;
    setFormData(prev => ({ ...prev, total_salary: total.toFixed(2) }));
  }, [formData.basic_salary, formData.hra, formData.ta, formData.oa]);

  // NEW: Auto-populate department when designation is selected
  useEffect(() => {
    if (formData.employee_designation && linkedData['Employee Designation']) {
      const selectedDesig = linkedData['Employee Designation'].find(
        d => (d.name === formData.employee_designation || d.designation_name === formData.employee_designation)
      );
      if (selectedDesig) {
        const autoDept = selectedDesig.reportTo || selectedDesig.report_to || selectedDesig.department || selectedDesig.department_name;
        if (autoDept && formData.department !== autoDept) {
          setFormData(prev => ({ ...prev, department: autoDept }));
        }
      }
    }
  }, [formData.employee_designation, linkedData]);


  // NEW: Fetch Companies from CompanyDetails API
  const fetchCompanies = async (url = baseUrl) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      
      let comps = [];
      const activeContext = localStorage.getItem('active_company');
      
      if (activeContext === 'All' || (!userObj.companies && !userObj.company_name && !userObj.company)) {
        const res = await axios.get(`${url || baseUrl}/api/user-companies`, { headers: getHeaders() });
        const details = res.data.companies || res.data || [];
        comps = details.map(d => d.restaurantName || d.company_name).filter(n => n);
      } else if (userObj.companies && userObj.companies.length > 0) {
        comps = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (userObj.company_name || userObj.company) {
        comps = [userObj.company_name || userObj.company];
      }
      const finalComps = comps.length > 0 ? [...new Set(comps)] : ['POS 8'];
      setCompanyOptions(finalComps);


      if (selectedCompanies.length === 0 && finalComps.length > 0) {
        if (activeContext && activeContext !== 'All' && finalComps.includes(activeContext)) {
          setSelectedCompanies([activeContext]);
        } else if (activeContext === 'All') {
          setSelectedCompanies(finalComps);
        } else if (!activeContext && finalComps.length === 1) {
          setSelectedCompanies([finalComps[0]]);
        }
      }

      if (finalComps.length > 0) {
        const branchPromises = finalComps.map(comp => 
          axios.get(`${url || baseUrl}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: { "X-Company-Name": comp, ...getHeaders() } })
            .then(res => ({ comp, data: res.data }))
        );
        const results = await Promise.all(branchPromises);
        const newMap = {};
        const allB = [];
        results.forEach(res => {
           const branches = res.data.map(b => typeof b === 'string' ? b : b.branch_name || b.branch || b.name || '').filter(b => b);
           newMap[res.comp] = branches;
           allB.push(...branches);
        });
        setCompanyBranchesMap(newMap);
        setAvailableBranches([...new Set(allB)]);
      }
    } catch (e) { console.error(e); }
  };

  const toggleCompanySelection = (comp) => {
    setSelectedCompanies(prev => {
      const isRemoving = prev.some(c => String(c).toLowerCase() === String(comp).toLowerCase());
      const next = isRemoving 
        ? prev.filter(c => String(c).toLowerCase() !== String(comp).toLowerCase()) 
        : [...prev, comp];
      
      // Update primary company field for ID generation
      const specificComps = next.filter(c => String(c).toLowerCase() !== 'all' && String(c) !== 'POS 8');
      if (specificComps.length > 0) {
        setFormData(p => ({ ...p, company: specificComps[0] }));
      }
      
      return next;
    });
  };

  // Helper str function for safety
  const str = (val) => String(val || '');





  // fetchNextEmployeeId:
  //   branchName = ""      → no branch selected at all (company-based ID)
  //   branchName = "test1" → branch selected (branch-based ID: TEST10001)
  //   skipLocalFallback = true → called from branch-selection aware effect; don't fall back to localStorage
  const fetchNextEmployeeId = async (companyName, branchName = "", skipLocalFallback = false) => {
    if (editingId) return;
    try {
      setNextIdLoading(true);
      let finalBranch = branchName;

      // Only fall back to localStorage when we are NOT explicitly tracking branch selection
      if (!finalBranch && !skipLocalFallback) {
        const activeBranch = localStorage.getItem('active_branch');
        const userStr = localStorage.getItem('user');
        let userObj = {};
        try { if (userStr) userObj = JSON.parse(userStr); } catch (_) {}
        finalBranch = (activeBranch && activeBranch !== 'All Branches' && activeBranch !== 'All')
          ? activeBranch
          : (userObj.branch_name || userObj.branch || "");
      }

      const url = `${baseUrl}/api/next-employee-id?company_name=${encodeURIComponent(companyName || "")}&branch_name=${encodeURIComponent(finalBranch || "")}`;
      const response = await axios.get(url, { headers: getHeaders() });
      if (response.data && response.data.nextId) {
        setFormData(prev => ({ ...prev, employee_id: response.data.nextId }));
      }
    } catch (err) {
      console.error("Error fetching next ID:", err);
    } finally {
      setNextIdLoading(false);
    }
  };

  // Re-fetch employee ID whenever company OR branch selection changes.
  useEffect(() => {
    if (!editingId && baseUrl !== null) {
      const primaryComp = selectedCompanies.find(c => String(c).toLowerCase() !== 'all' && String(c) !== 'POS 8') || selectedCompanies[0] || "";
      const activeBr = selectedBranches.length > 0 ? selectedBranches[0] : "";
      
      // If the user has explicitly selected companies OR branches, we SKIP the local fallback.
      // This ensures that if they check a Company but NOT a Branch, they get the Company ID prefix.
      const userInteracted = selectedCompanies.length > 0 || selectedBranches.length > 0;
      
      fetchNextEmployeeId(primaryComp, activeBr, userInteracted);
    }
  }, [selectedCompanies, selectedBranches, editingId, baseUrl]);

  // NEW: Fetch single employee details (to ensure all fields like department are loaded)
  const fetchSingleEmployee = async (id, currentBaseUrl = baseUrl) => {
    try {
      const url = `${currentBaseUrl}/api/add-employee/${id}`;
      const response = await axios.get(url, { headers: getHeaders() });
      const emp = response.data;
      if (emp) {
        setFormData(prev => {
          let phoneIdxVal = emp.phone_number || emp.phoneNumber || prev.phone_number;
          if (emp.phone_number || emp.phoneNumber) {
            const phone = emp.phone_number || emp.phoneNumber;
            const isdMatch = isdCodes.find(c => phone.startsWith(c.code));
            if (isdMatch) {
              setSelectedISDCode(isdMatch.code);
              phoneIdxVal = phone.slice(isdMatch.code.length);
            }
          }
          const normalized = normalizeEmployeeData(emp);
          return {
            ...prev,
            ...normalized,
            phone_number: phoneIdxVal,
            password: '',
          };
        });
        setSelectedBranches(resolveList(emp, ['branch_names', 'branch_name', 'branches', 'branch']));
        setSelectedCompanies(resolveList(emp, ['company_names', 'company_name', 'companies', 'company']));
        
        const userCompanies = resolveList(emp, ['company_names', 'company_name', 'companies', 'company']);
        if (userCompanies.length > 0) {
          // Set primary company from first entry to ensure ID generation context is correct
          const specific = userCompanies.find(c => String(c).toLowerCase() !== 'all' && String(c) !== 'POS 8');
          if (specific) {
            setFormData(p => ({ ...p, company: specific }));
          }
        }
        if (emp.profile_image && emp.profile_image.startsWith('data:')) {
          setImagePreview(emp.profile_image);
        } else if (emp.profileImage && emp.profileImage.startsWith('data:')) {
          setImagePreview(emp.profileImage);
        }
      }
    } catch (err) {
      console.error("Error fetching full employee details:", err);
    }
  };



  // NEW: Robust normalization of employee data between camelCase (backend) and snake_case (metadata)
  const normalizeEmployeeData = (emp) => {
    if (!emp) return {};
    const keyMap = {
      phoneNumber: 'phone_number',
      employeeId: 'employee_id',
      employeeDesignation: 'employee_designation',
      employeeType: 'employee_type',
      dateOfBirth: 'date_of_birth',
      dateOfJoining: 'date_of_joining',
      maritalStatus: 'marital_status',
      idNumber: 'id_number',
      idExpiry: 'id_expiry',
      basicSalary: 'basic_salary',
      totalSalary: 'total_salary',
      bankName: 'bank_name',
      accountHolderName: 'account_holder_name',
      accountNumber: 'account_number',
      branchCode: 'branch_code',
      previousExperience: 'previous_experience',
      healthInfo: 'health_info',
      familyDetails: 'family_details',
      profileImage: 'profile_image',
      profile_image: 'profile_image'
    };
    
    const normalized = { ...emp };
    Object.entries(keyMap).forEach(([camel, snake]) => {
      if (emp[camel] !== undefined) normalized[snake] = emp[camel];
      if (emp[snake] !== undefined) normalized[snake] = emp[snake];
    });
    
    // Ensure department is handled (it seems consistent but just in case)
    if (emp.department) normalized.department = emp.department;
    
    return normalized;
  };

  // NEW: Helper to resolve lists (companies/branches) from various possible fields
  const resolveList = (emp, fields) => {
    for (const field of fields) {
      const val = emp[field];
      if (val) {
        if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(v => v);
        if (typeof val === 'string') return val.split(',').map(v => v.trim()).filter(v => v);
      }
    }
    return [];
  };

  // Unified restore logic for formData (handles initial edit, restore from sub-pages like Designation/Department/Company, preserves editing state)
  useEffect(() => {
    if (location.state?.editingEmployee) {
      // Initial edit load
      const emp = location.state.editingEmployee;
      const parsedId = emp._id || emp.id || emp.employeeId || emp.employee_id;
      const normalizedData = normalizeEmployeeData(emp);
      
      const parsedFormData = { 
        ...normalizedData, 
        password: ''
      };
      
      setEditingId(parsedId);
      setIsEditingDraft(false);
      setFormData(parsedFormData);
      // Set image preview
      if (emp.profileImage) {
        setImagePreview(emp.profileImage.startsWith('data:') ? emp.profileImage : null);
      }
      setSelectedBranches(resolveList(emp, ['branch_names', 'branch_name', 'branches', 'branch']));
      setSelectedCompanies(resolveList(emp, ['company_names', 'company_name', 'companies', 'company']));
    } else if (location.state?.formData) {
      // Restore from sub-page (designation/type/department/company)
      const restored = location.state.formData;
      const savedCompany = location.state.newCompanyName;

      // Capture new items from sub-pages
      const savedDesignation = location.state.newEmployeeDesignation || location.state.newDesignation;
      const savedType = location.state.newEmployeeType || location.state.newType;
      const savedDepartment = location.state.newDepartment || location.state.newDepartmentName;

      // Set ISD if provided in state
      if (location.state.selectedISDCode) {
        setSelectedISDCode(location.state.selectedISDCode);
      }

      // Check if editing (has _id OR passed isEditing flag)
      if (restored._id || location.state.editingId) {
        setEditingId(restored._id || location.state.editingId);
        setIsEditingDraft(false);
        // Set image preview
        if (restored.profileImage) {
          setImagePreview(restored.profileImage.startsWith('data:') ? restored.profileImage : null);
        }
      } else {
        setEditingId(null);
      }

      // If we got a new company (or just returned), update it and switch tab
      if (savedCompany) {
        restored.company = savedCompany;
        setActiveTab('personal');
      }

      // Update Designation, Type, Department if created and switch to Employment tab
      if (savedDesignation) {
        restored.employeeDesignation = savedDesignation;
        setActiveTab('employment');
      }
      if (savedType) {
        restored.employeeType = savedType;
        setActiveTab('employment');
      }
      if (savedDepartment) {
        restored.department = savedDepartment;
        setActiveTab('employment');
      }

      if (restored.branch_names) {
        setSelectedBranches(restored.branch_names);
      }
      setFormData(restored);

      // Refetch lists to include new items immediately
      const url = baseUrl || "";
      fetchMetadata(url);
      fetchCompanies(url); // Refresh companies
    }
  }, [location.state, baseUrl]);



  const toggleBranchSelection = (branch) => {
    setSelectedBranches(prev => 
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };
  // NEW: Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profileImage: reader.result })); // Base64
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleISDCodeSelect = (code) => {
    setSelectedISDCode(code);
    setShowISDCodeDropdown(false);
  };
  const handlePhoneNumberChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    const maxDigits = digitLengths[selectedISDCode] || 10;
    if (v.length <= maxDigits) {
      setFormData(prev => ({ ...prev, phone_number: v }));
    }
  };
  const getMaxDigits = () => digitLengths[selectedISDCode] || 10;
  const openCreationPanel = async (docTypeName, fieldId) => {
    setCreationPanel(prev => ({ ...prev, open: true, docType: docTypeName, fields: [], formValues: {}, saving: false, error: null, fieldForId: fieldId }));
    try {
      const url = `${baseUrl}/api/doctypes/${encodeURIComponent(docTypeName)}`;
      const response = await axios.get(url, { headers: getHeaders() });
      const allFields = (response.data.fields || []).filter(f => !f.hidden && f.type !== 'Section Break' && f.type !== 'Column Break');
      const initValues = {};
      allFields.forEach(f => { initValues[f.id] = ''; });
      setCreationPanel(prev => ({ ...prev, fields: allFields, formValues: initValues }));
    } catch (err) {
      console.error('Failed to fetch DocType fields for panel:', err);
      setCreationPanel(prev => ({ ...prev, error: 'Failed to load form fields. Please try again.', fields: [] }));
    }
  };

  const saveCreationPanel = async () => {
    const { docType, formValues, fieldForId } = creationPanel;
    const endpointMap = {
      'Employee Designation': 'employee-designations',
      'Employee Type': 'employee-types',
      'Employee Department': 'departments'
    };
    const endpoint = endpointMap[docType] || docType.toLowerCase().replace(/\s+/g, '-') + 's';
    
    // Auto-populate company and branch assignments from state if not overridden
    const finalCompanyNames = formValues.company_names || selectedCompanies;
    const finalBranchNames = formValues.branch_names || selectedBranches;
    
    const payload = {
      ...formValues,
      company_names: finalCompanyNames,
      branch_names: finalBranchNames,
      company_name: finalCompanyNames[0] || ''
    };

    setCreationPanel(prev => ({ ...prev, saving: true, error: null }));
    try {
      const url = `${baseUrl}/api/${endpoint}`;
      const response = await axios.post(url, payload, { headers: getHeaders() });
      const newName = response.data?.name || response.data?.designation_name || response.data?.type_name || response.data?.department_name || Object.values(response.data || {})[0] || '';
      // Refresh linked data for this doctype
      await fetchLinkedData(docType);
      // Auto-select the newly created value in the form
      if (fieldForId) {
        setFormData(prev => ({ ...prev, [fieldForId]: newName }));
      }
      setCreationPanel({ open: false, docType: null, fields: [], formValues: {}, saving: false, error: null, fieldForId: null });
    } catch (err) {
      console.error('Error saving new record:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || 'Save failed. Please try again.';
      setCreationPanel(prev => ({ ...prev, saving: false, error: msg }));
    }
  };

  const renderField = (field) => {
    if (field.hidden) return null;

    // Specialized handling for ID and Phone
    if (field.id === 'phone_number') {
       return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ position: 'relative', width: '100px' }}>
              <button
                type="button"
                onClick={() => setShowISDCodeDropdown(!showISDCodeDropdown)}
                style={{ width: '100%', padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                {selectedISDCode}
              </button>
              {showISDCodeDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, width: '200px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', marginTop: '4px' }}>
                  {isdCodes.map(c => (
                    <div key={c.code} onClick={() => handleISDCodeSelect(c.code)} style={{ padding: '10px 15px', cursor: 'pointer' }} className="isd-option">
                      {c.code} ({c.country})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input
              type="text"
              name="phone_number"
              value={formData.phone_number}
              onChange={handlePhoneNumberChange}
              placeholder={`Enter ${getMaxDigits()} digits`}
              style={{ flex: 1, padding: '12px', border: '1px solid #e2e8f0', borderRadius: '12px' }}
              required={field.mandatory}
            />
          </div>
       );
    }

    const commonProps = {
      id: field.id,
      name: field.id,
      value: formData[field.id] || '',
      onChange: handleChange,
      required: field.mandatory,
      className: "form-input-dynamic",
      style: { width: '100%', padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '15px' }
    };

    switch (field.type) {
      case 'Select':
        return (
          <select {...commonProps}>
             <option value="">Select {field.label}</option>
             {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'Date':
        return <CustomDatePicker 
          value={formData[field.id]} 
          onChange={(val) => setFormData(prev => ({ ...prev, [field.id]: val }))} 
          placeholder={`Select ${field.label}`}
        />;
      case 'Password':
        return (
          <input 
            type="password" 
            {...commonProps} 
            placeholder={editingId ? "Leave blank to keep current" : `Enter ${field.label}`}
            required={field.mandatory && !editingId}
          />
        );
      case 'Number':
        return <input type="number" {...commonProps} />;
      case 'Text':
        return <textarea {...commonProps} rows="3" />;
      case 'Attach Image':
        return (
           <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
             <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: '#f1f5f9', borderRadius: '12px', cursor: 'pointer', color: '#475569', fontWeight: '600' }}>
               <FaUpload /> Upload Image
               <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
             </label>
             {imagePreview && (
               <div style={{ position: 'relative' }}>
                 <img src={imagePreview} alt="Preview" style={{ width: '50px', height: '50px', borderRadius: '10px', objectFit: 'cover' }} />
                 <button onClick={() => {setImagePreview(null); setFormData(p => ({...p, profile_image: ''}))}} style={{ position: 'absolute', top: '-5px', right: '-5px', width: '18px', height: '18px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                   <FaTimes />
                 </button>
               </div>
             )}
           </div>
        );
      case 'Table':
        if (field.link_doctype) {
           const tableOpts = (linkedData[field.link_doctype] || []).map(opt =>
             typeof opt === 'string' ? opt : (opt.name || opt.designation_name || opt.type_name || opt.department_name || Object.values(opt)[0] || '')
           ).filter(Boolean);
           return (
             <SearchableSelect
               value={formData[field.id] || ''}
               onChange={(val) => setFormData(prev => ({ ...prev, [field.id]: val }))}
               options={tableOpts}
               placeholder={`Select ${field.label}`}
               allowCreateNew={true}
               createNewLabel={field.label}
               onCreateRequest={() => openCreationPanel(field.link_doctype, field.id)}
             />
           );
        }
        return <input type="text" {...commonProps} />;
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  // Helper to handle Company Selection from Dropdown
  const handleCompanyChange = (e) => {
    const value = e.target.value;
    if (value === 'create_new') {
      navigate('/company-details', {
        state: {
          returnTo: '/add-employee',
          preservedState: {
            formData: { ...formData, company: '' },
            selectedISDCode: selectedISDCode,
            isEditing: !!editingId,
            editingId: editingId
          }
        }
      });
    } else {
      setFormData(prev => ({ ...prev, company: value }));
    }
  };
  // Validation - Full for submit
  const validateForm = () => {
    const active_company = localStorage.getItem('active_company');
    const isGroupOrCompanyAdmin = isGroupAdmin || isCompanyAdmin;
    
    if (isGroupOrCompanyAdmin) {
      if (isGroupAdmin && selectedCompanies.length === 0) {
        setError('Please select at least one company.');
        return false;
      }
    }
    if (baseUrl === null) {
      setError('Server configuration is loading. Please wait...');
      return false;
    }

    if (!metadata) return true;

    // Minimal required fields from metadata
    let required = metadata.fields.filter(f => f.mandatory && !f.hidden).map(f => f.id);
    
    if (editingId) {
      // If editing, password is not mandatory even if metadata says so
      required = required.filter(id => id !== 'password');
    } else {
      // If creating, ensure password is in the list
      if (!required.includes('password')) {
        required.push('password');
      }
    }

    const missingFields = required.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      const labels = missingFields.map(id => {
        const field = metadata.fields.find(f => f.id === id);
        return field ? field.label : (id === 'password' ? 'Password' : id);
      });
      setError(`Please fill in the following required fields: ${labels.join(', ')}.`);
      return false;
    }

    // Validate salary fields only if provided (optional now)
    const salaryFields = ['basic_salary', 'hra', 'ta', 'oa'];
    for (const field of salaryFields) {
      if (formData[field] && (isNaN(formData[field]) || parseFloat(formData[field]) < 0)) {
        setError(`${field.replace(/_/g, ' ').toUpperCase()} must be a valid positive number if provided.`);
        return false;
      }
    }

    const phoneMaxDigits = getMaxDigits();
    if (formData.phone_number.length !== phoneMaxDigits) {
      setError(`Phone number must be exactly ${phoneMaxDigits} digits.`);
      return false;
    }

    // Date validations only if provided
    const dates = ['date_of_birth', 'date_of_joining', 'id_expiry'];
    for (const dateField of dates) {
      if (formData[dateField]) {
        const date = new Date(formData[dateField]);
        if (isNaN(date.getTime())) {
          setError(`Invalid date format for ${dateField.replace(/_/g, ' ').toLowerCase()}. Use YYYY-MM-DD.`);
          return false;
        }
      }
    }

    // Email validation (always required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    return true;
  };
  // Handle confirmation submit
  const handleConfirmSubmit = async () => {
    setShowConfirmation(false);
    if (!validateForm()) return;
    setLoading(true);
    setMessage('');
    setError('');
    try {
      let url, method, dataToSend;
      // Normal create/update
      url = baseUrl ? `${baseUrl}/api/add-employee` : '/api/add-employee';
      method = editingId ? 'put' : 'post';
      if (editingId) url += `/${editingId}`;
      // Map metadata-driven snake_case keys to camelCase keys expected by the backend
      const keyMap = {
        phone_number: 'phoneNumber',
        employee_id: 'employeeId',
        employee_designation: 'employeeDesignation',
        employee_type: 'employeeType',
        date_of_birth: 'dateOfBirth',
        date_of_joining: 'dateOfJoining',
        marital_status: 'maritalStatus',
        id_number: 'idNumber',
        id_expiry: 'idExpiry',
        basic_salary: 'basicSalary',
        total_salary: 'totalSalary',
        bank_name: 'bankName',
        account_holder_name: 'accountHolderName',
        account_number: 'accountNumber',
        branch_code: 'branchCode',
        previous_experience: 'previousExperience',
        health_info: 'healthInfo',
        family_details: 'familyDetails',
        profile_image: 'profileImage'
      };

      const mappedData = { ...formData };
      Object.entries(keyMap).forEach(([snake, camel]) => {
        if (mappedData[snake] !== undefined) {
          mappedData[camel] = mappedData[snake];
        }
      });

      const activeContext = localStorage.getItem('active_company') || 'All';
      const activeBranchContext = localStorage.getItem('active_branch') || 'All Branches';
      
      let finalCompanies = selectedCompanies;
      if (finalCompanies.length === 0 && (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 0) {
        finalCompanies = ['All Companies'];
      }
      
      let finalBranches = selectedBranches;
      if (finalBranches.length === 0 && (isGroupAdmin || isCompanyAdmin) && availableBranches.length > 0) {
        finalBranches = ['All Branches'];
      }

      dataToSend = {
        ...mappedData,
        phoneNumber: `${selectedISDCode}${formData.phone_number}`,
        hra: formData.hra || '',
        ta: formData.ta || '',
        oa: formData.oa || '',
        basicSalary: formData.basic_salary || '',
        branch_name: finalBranches[0] || "",
        branch_names: finalBranches,
        company: finalCompanies[0] || "", // Ensure 'company' is synced with finalCompanies
        company_name: finalCompanies[0] || "",
        company_names: finalCompanies,
      };
      if (editingId && !dataToSend.password) {
        delete dataToSend.password;
      }

      const headers = getHeaders();
      const response = await axios({
        method,
        url,
        data: dataToSend,
        headers,
      });
      const successMsg = editingId ? 'Employee updated successfully!' : 'Employee created successfully!';
      // Reset form
      setFormData({
        name: '', phone_number: '', email: '', gender: '', date_of_birth: '', date_of_joining: '', company: 'POS 8', status: 'Active', salutation: '', marital_status: '', id_number: '', id_expiry: '', address: '', employee_designation: '', employee_type: '',
        basic_salary: '', hra: '', ta: '', oa: '', total_salary: '', username: '', password: '', bank_name: '', account_holder_name: '', account_number: '', branch_code: '',
        nationality: '', education: '', previous_experience: '', skills: '', health_info: '', family_details: '', profile_image: '', department: '', employee_id: '',
      });
      setImagePreview(null);
      setSelectedISDCode("+971");
      setEditingId(null);

      setTimeout(() => {
        // NEW: Check for returnTo state
        if (location.state && location.state.returnTo) {
          const newId = response.data._id || response.data.employee?._id || response.data.id;
          navigate(location.state.returnTo, {
            state: {
              newGeneralEmployeeId: newId,
              preservedState: location.state.preservedState
            }
          });
        } else {
          navigate('/admin'); // Navigate to employee list page on success
        }
      }, 2000);
      setMessage(successMsg);
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${editingId ? 'update' : 'create'} employee`;
      // If error, do not navigate, stay on form
      setError(errorMsg);
      console.error('Error details:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (editingId && !canWrite) {
      setPermModalMsg("You do not have permission to edit employees.");
      setShowPermModal(true);
      return;
    }
    if (!editingId && !canCreate) {
      setPermModalMsg("You do not have permission to create employees.");
      setShowPermModal(true);
      return;
    }
    if (!validateForm()) return;
    // Show assignment modal for group/company admins before saving
    if (!showAssignmentModal && (showCompanyAssign || showBranchAssign)) {
      setShowAssignmentModal(true);
      return;
    }
    // If modal already open, validate company selection then go to confirmation
    if (showAssignmentModal && showCompanyAssign && selectedCompanies.length === 0) {
      setError('Please select at least one company.');
      return;
    }
    setShowAssignmentModal(false);
    setShowConfirmation(true);
  };
  const cancelEdit = () => {
    setEditingId(null);
    // Reset form to snake_case IDs
    setFormData({
      name: '', phone_number: '', email: '', gender: '', date_of_birth: '', date_of_joining: '', company: 'POS 8', status: 'Active', salutation: '', marital_status: '', id_number: '', id_expiry: '', address: '', employee_designation: '', employee_type: '',
      basic_salary: '', hra: '', ta: '', oa: '', total_salary: '', username: '', password: '', bank_name: '', account_holder_name: '', account_number: '', branch_code: '',
      nationality: '', education: '', previous_experience: '', skills: '', health_info: '', family_details: '', profile_image: '', department: '', employee_id: '',
    });
    setImagePreview(null);
    setSelectedISDCode("+971");
    setActiveTab('details');
  };

  const getFieldsForTab = (tabKey) => {
    if (!metadata) return [];
    return metadata.fields.filter(field => getFieldTab(field.id) === tabKey);
  };

  // NEW: Get required fields list dynamically
  const getRequiredFields = () => {
    if (!metadata) return ['Full Name', 'Phone', 'Email', 'Username', 'Password'];
    const required = metadata.fields.filter(f => f.mandatory && !f.hidden).map(f => f.label);
    if (!editingId) {
      required.push('Password');
    }
    return required;
  };
  if (permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', color: '#fff', fontSize: '1.2rem', fontWeight: '600' }}>
          <FaUserTie style={{ fontSize: '48px', marginBottom: '20px', color: '#fff' }} />
          <p>Loading Permissions...</p>
        </div>
      </div>
    );
  }

  if (!canRead && !isCompanyAdmin && !isGroupAdmin && !permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', maxWidth: '500px' }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Access Denied</h2>
          <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>You do not have permission to view the Add Employee page.</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin')} style={{ marginTop: '20px', borderRadius: '50px', padding: '10px 30px', background: '#3498db', border: 'none', color: '#fff' }}>Back to Admin</button>
        </div>
      </div>
    );
  }

  const showCompanyAssign = (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 0;
  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';
  const showBranchAssign = (isGroupAdmin || isCompanyAdmin) && !isSpecificBranchActive && availableBranches.length > 0;

  return (
    <div className="create-item-container main-item-page">
      <div className="item-header-section">
        <div className="header-left">
          <button
            onClick={() => {
              if (location.state && location.state.returnTo) {
                navigate(location.state.returnTo, { state: { preservedState: location.state.preservedState } });
              } else {
                navigate('/admin');
              }
            }}
            className="header-back-btn"
            disabled={loading}
          >
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1>{editingId ? 'Edit Employee' : 'Add New Employee'}</h1>
        </div>
        <div className="header-right header-actions">
          {(isGroupAdmin || isCompanyAdmin || checkIsAdmin(user || JSON.parse(localStorage.getItem('user') || '{}'))) && (
            <button
              type="button"
              onClick={() => setShowCustomizeModal(true)}
              className="save-btn"
              style={{ backgroundColor: 'rgb(46, 204, 113)', minWidth: 'auto', padding: '0 16px' }}
              disabled={loading}
            >
              <FaCog /> Customize
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowRequiredFields(true)}
            className="save-btn"
            style={{ backgroundColor: '#f59e0b', minWidth: 'auto', padding: '0 16px' }}
            disabled={loading}
          >
            <FaExclamationTriangle /> Required
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              className="save-btn"
              style={{ backgroundColor: '#94a3b8', minWidth: 'auto', padding: '0 16px' }}
              disabled={loading}
            >
              Cancel Edit
            </button>
          )}
          <button
            type="submit"
            form="employeeForm"
            className="save-btn"
            disabled={loading}
          >
            <FaSave /> {loading ? 'Processing...' : (editingId ? 'Update' : 'Save')}
          </button>
        </div>
      </div>

      <div className="item-scroll-body">
        {error && (
          <div style={{ width: '100%', maxWidth: '1200px', background: '#fee2e2', color: '#ef4444', padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
            <FaTimes /> {error}
          </div>
        )}
        {message && (
          <div style={{ width: '100%', maxWidth: '1200px', background: '#dcfce7', color: '#10b981', padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
            <FaClock /> {message}
          </div>
        )}

        <form id="employeeForm" onSubmit={handleSubmit} className="form-sections-wrapper" style={{ width: '100%', maxWidth: '1200px' }}>
          {metadata ? (
            <>
              <div className="form-section-card" style={{ padding: '30px' }}>
                {/* Tab Navigation Inside Card */}
                {(() => {
                  const tabs = getTabs();
                  // set default tab if not set
                  if (tabs.length > 0 && !activeTabId) {
                    setTimeout(() => setActiveTabId(tabs[0].key), 0);
                    return null;
                  }
                  
                  return tabs.length > 0 && (
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px' }}>
                      {tabs.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveTabId(tab.key)}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: '14px',
                            background: activeTabId === tab.key ? '#eff6ff' : '#ffffff',
                            color: activeTabId === tab.key ? '#604BE8' : '#64748b',
                            border: activeTabId === tab.key ? '1px solid #604BE8' : '1px solid #e2e8f0',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {tab.label}
                        </button>
                      ))}

                    </div>
                  );
                })()}

                {/* Tabbed Content Inside Card (No section header) */}
                {(() => {

                  const tab = getTabs().find(t => t.key === activeTabId) || getTabs()[0];
                  if (!tab) return null;
                  
                  const fields = getFieldsForTab(tab.key).filter(f => f.type !== 'Section Break');
                  if (fields.length === 0 && tab.key !== getTabs()[0].key && tab.key !== 'credentials') return null;
                  
                  return (
                    <div key={tab.key}>
                      <div className="form-grid">
                        {tab.key === getTabs()[0].key && (
                          <div className="form-group">
                            <label>Employee ID {nextIdLoading && <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>(Updating...)</span>}</label>
                            <input 
                              type="text" 
                              className="input"
                              value={formData.employee_id || formData.employeeId || ''} 
                              readOnly 
                              placeholder="Auto-generated"
                              style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }} 
                            />
                          </div>
                        )}
                        
                        {fields.filter(f => f.id !== 'employee_id').map(field => (
                          <div key={field.id} className="form-group" style={{ gridColumn: field.type === 'Text' ? 'span 2' : 'auto' }}>
                            <label style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              {field.label} {field.mandatory ? <span className="required" style={{ color: '#ef4444' }}>*</span> : ''}
                            </label>
                            {renderField(field)}
                          </div>
                        ))}
                        
                        {(tab.key === 'credentials' || tab.label.toLowerCase().includes('credentials')) && metadata && !metadata.fields.find(f => f.id === 'password') && (
                          <div className="form-group">
                            <label style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              Password {editingId ? '(Leave blank to keep current)' : <span className="required" style={{ color: '#ef4444' }}>*</span>}
                            </label>
                            {renderField({ id: 'password', label: 'Password', type: 'Password', mandatory: !editingId })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
             <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column', gap: '20px' }}>
               <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f1f5f9', borderTop: '4px solid #604BE8', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
               <p style={{ color: '#64748b', fontWeight: '600' }}>Loading Employee Configuration...</p>
             </div>
          )}
        </form>
      </div>


      

      {/* Centered Permission Denied Modal */}
      {showPermModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: '#fff', padding: '30px', borderRadius: '15px', textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxWidth: '400px', width: '90%'
          }}>
            <div style={{ color: '#e74c3c', fontSize: '3rem', marginBottom: '15px' }}>⚠️</div>
            <h3 style={{ marginBottom: '10px', color: '#2c3e50' }}>Permission Denied</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>{permModalMsg}</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowPermModal(false)}
              style={{ background: '#3498db', border: 'none', borderRadius: '50px', padding: '10px 30px', fontWeight: 'bold' }}
            >
              Understood
            </button>
          </div>
        </div>
      )}

      {/* NEW: Required Fields Modal */}
      {showRequiredFields && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
          onClick={() => setShowRequiredFields(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '30px',
              borderRadius: '15px',
              textAlign: 'left',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
              maxWidth: '400px',
              width: '90%',
              position: 'relative',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                color: '#f39c12',
                fontSize: '1.5rem',
                marginBottom: '15px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <FaExclamationTriangle /> Required Fields
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {getRequiredFields().map((field, index) => (
                <li
                  key={index}
                  style={{
                    padding: '10px',
                    borderBottom: '1px solid #ecf0f1',
                    color: '#2c3e50',
                    fontSize: '1rem'
                  }}
                >
                  • {field}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                onClick={() => setShowRequiredFields(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s'
                }}
                onMouseOver={(e) => (e.target.style.backgroundColor = '#7f8c8d')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#95a5a6')}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal - Warning before submit */}
      {showConfirmation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '30px',
              borderRadius: '15px',
              textAlign: 'center',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
              maxWidth: '450px',
              width: '90%',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                color: '#f39c12',
                fontSize: '1.5rem',
                marginBottom: '15px',
                fontWeight: 'bold'
              }}
            >
              Confirm Action
            </div>
            <p style={{ margin: 0, color: '#2c3e50', fontSize: '1rem', lineHeight: '1.5' }}>
              Are you sure you want to {editingId ? 'update' : 'save'} this employee? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button
                onClick={handleConfirmSubmit}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s'
                }}
                onMouseOver={(e) => (e.target.style.backgroundColor = '#2980b9')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#3498db')}
              >
                Submit
              </button>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s'
                }}
                onMouseOver={(e) => (e.target.style.backgroundColor = '#7f8c8d')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#95a5a6')}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company & Branch Assignment Modal — matches CreateCustomerPage design */}
      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>Employee Assignments</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {showCompanyAssign && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#604BE8', display: 'flex', alignItems: 'center', gap: '8px' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedCompanies.includes(comp) ? '#eff2f9' : '#f8fafc', borderRadius: '12px', border: `1.5px solid ${selectedCompanies.includes(comp) ? '#604BE8' : '#C3CDE4'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => { setSelectedCompanies(prev => e.target.checked ? [...prev, comp] : prev.filter(c => c !== comp)); }} style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {showBranchAssign && (
                <div className="assignment-card">
                  <h4 style={{ color: '#06D6A0', display: 'flex', alignItems: 'center', gap: '8px' }}><FaMapMarkerAlt /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {availableBranches.map((branch, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: selectedBranches.includes(branch) ? '#ecfdf5' : '#f8fafc', borderRadius: '12px', border: `1.5px solid ${selectedBranches.includes(branch) ? '#06D6A0' : '#d1fae5'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedBranches.includes(branch)} onChange={(e) => { setSelectedBranches(prev => e.target.checked ? [...prev, branch] : prev.filter(b => b !== branch)); }} style={{ width: '18px', height: '18px', accentColor: '#06D6A0' }} />
                        {branch}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleConfirmSubmit} disabled={loading || (showCompanyAssign && selectedCompanies.length === 0)}>
                {loading ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => fetchMetadata(baseUrl)}
        targetDocType={customizationTarget}
      />

      {/* Dynamic Inline Creation Side Panel */}
      {creationPanel.open && (
        <>
          <div
            onClick={() => !creationPanel.saving && setCreationPanel(prev => ({ ...prev, open: false }))}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(3px)', zIndex: 10100 }}
          />
          <div style={{
            position: 'fixed', top: 0, right: 0, height: '100%', width: '420px', maxWidth: '95vw',
            background: '#ffffff', zIndex: 10101, boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
            display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s cubic-bezier(0.4,0,0.2,1)'
          }}>
            {/* Panel Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 28px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Create New {creationPanel.docType}</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>Fill in the details and save</p>
              </div>
              <button
                onClick={() => !creationPanel.saving && setCreationPanel(prev => ({ ...prev, open: false }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#64748b', display: 'flex', alignItems: 'center', padding: '4px' }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Panel Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '28px' }}>
              {creationPanel.error && (
                <div style={{ padding: '14px 18px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '14px', marginBottom: '20px' }}>
                  {creationPanel.error}
                </div>
              )}
              {creationPanel.fields.length === 0 && !creationPanel.error && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: '15px' }}>Loading fields...</div>
              )}
              {creationPanel.fields.map(f => (
                <div key={f.id} style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {f.label}{f.mandatory && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                  </label>
                  {f.type === 'Check' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!creationPanel.formValues[f.id]}
                        onChange={e => setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, [f.id]: e.target.checked ? 1 : 0 } }))}
                        style={{ width: '18px', height: '18px', accentColor: '#604BE8', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>{f.label}</span>
                    </label>
                  ) : f.type === 'Select' ? (
                    <select
                      value={creationPanel.formValues[f.id] || ''}
                      onChange={e => setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, [f.id]: e.target.value } }))}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#f8fafc', color: '#0f172a', outline: 'none' }}
                    >
                      <option value="">Select {f.label}</option>
                      {(f.options || '').split('\n').filter(Boolean).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : f.type === 'Date' ? (
                    <input
                      type="date"
                      value={creationPanel.formValues[f.id] || ''}
                      onChange={e => setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, [f.id]: e.target.value } }))}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#f8fafc', color: '#0f172a', outline: 'none' }}
                    />
                  ) : f.type === 'Text' ? (
                    <textarea
                      rows={3}
                      value={creationPanel.formValues[f.id] || ''}
                      onChange={e => setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, [f.id]: e.target.value } }))}
                      placeholder={`Enter ${f.label}`}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#f8fafc', color: '#0f172a', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  ) : (
                    <input
                      type={f.type === 'Number' ? 'number' : 'text'}
                      value={creationPanel.formValues[f.id] || ''}
                      onChange={e => setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, [f.id]: e.target.value } }))}
                      placeholder={`Enter ${f.label}`}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#f8fafc', color: '#0f172a', outline: 'none', boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Multi-Tenancy Assignment inside inline creation */}
            {(showCompanyAssign || showBranchAssign) && (
              <div style={{ padding: '0 28px 24px 28px' }}>
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '14px', fontWeight: '700' }}>Assignments</h4>
                  
                  {showCompanyAssign && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Assign to Companies *</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {companyOptions.map(comp => (
                          <label key={comp} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={(creationPanel.formValues.company_names || selectedCompanies).includes(comp)}
                              onChange={(e) => {
                                const current = creationPanel.formValues.company_names || selectedCompanies;
                                const updated = e.target.checked ? [...current, comp] : current.filter(c => c !== comp);
                                setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, company_names: updated } }));
                              }}
                              style={{ width: '16px', height: '16px', accentColor: '#604BE8', cursor: 'pointer' }}
                            />
                            {comp}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {showBranchAssign && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Assign to Branches</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {availableBranches.map(br => (
                          <label key={br} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={(creationPanel.formValues.branch_names || selectedBranches).includes(br)}
                              onChange={(e) => {
                                const current = creationPanel.formValues.branch_names || selectedBranches;
                                const updated = e.target.checked ? [...current, br] : current.filter(b => b !== br);
                                setCreationPanel(prev => ({ ...prev, formValues: { ...prev.formValues, branch_names: updated } }));
                              }}
                              style={{ width: '16px', height: '16px', accentColor: '#06D6A0', cursor: 'pointer' }}
                            />
                            {br}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* Panel Footer */}
            <div style={{ padding: '20px 28px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '12px', background: '#fafbfc' }}>
              <button
                type="button"
                onClick={() => setCreationPanel(prev => ({ ...prev, open: false }))}
                disabled={creationPanel.saving}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#ffffff', color: '#475569', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveCreationPanel}
                disabled={creationPanel.saving || creationPanel.fields.length === 0}
                style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: '#604BE8', color: '#ffffff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {creationPanel.saving ? 'Saving...' : `Save ${creationPanel.docType || ''}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddEmployee;
